import { SegmentService } from "@main-services/segment.service";
import { CurrencyService } from "@main-services/currency";
import { FiatService } from "@main-services/currency/fiat.service";
import { Directive, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Params } from "@angular/router";
import { Trade, Wallet } from "@models";
import { Provider } from "@models/interfaces";
import { Helper } from "@helpers/functions";
import { WalletService } from "../services/wallet/wallet.service";
import { TradableListService } from "../services/currency/tradable_list.service";
import { Currency } from "@models/interfaces/currency";
import { equal } from "@helpers/utils";
import { Transaction } from "@models/transaction";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { PaginationComponent } from "../../core/components/pagination/pagination.component";
import { InfoService } from "@core-services/info.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Directive()
export abstract class WalletComponent<T extends Transaction, C extends Currency, W extends Wallet> implements OnInit {
	@ViewChild(PaginationComponent, { static: true })
	public paginationComponent: PaginationComponent<T>;

	private _map: Map<string, T[]>;

	public tid: string = "";

	public walletId = "";
	public walletName: Observable<string> = EMPTY;
	protected _currency: C = null;
	protected filterActive: boolean = false;
	private _transactionDateFormat: string = "";
	public abstract readonly transactionFilters: string[];

	public abstract readonly trackingBasePath: string;

	protected abstract modalService: ModalService;
	protected abstract masterdataService: MasterdataService;
	protected abstract tradableListService: TradableListService;
	protected abstract fiatService: FiatService;
	protected abstract walletService: WalletService<W, T>;
	protected abstract infoService: InfoService;
	protected abstract route: ActivatedRoute;
	protected abstract currencyService: CurrencyService<C>;
	protected abstract segmentService: SegmentService;
	protected abstract rudderstackService: RudderstackService;
	protected _wallet: W = null;

	constructor(protected activatedRoute: ActivatedRoute) {}

	public ngOnInit(): void {
		this.walletName = this.activatedRoute.params.pipe(
			switchMap((params) =>
				this.walletService.getList().pipe(map((walletList) => ({ list: walletList, id: params["pid"] })))
			),
			map(({ list, id }) => list.find((wallet) => wallet.getId() === id)),
			map((wallet) => wallet.getName())
		);

		this.walletService
			.getTransactionUpdateReceived()
			.pipe(untilDestroyed(this))
			.subscribe(() => this.paginationComponent.loadPage(this.paginationComponent.pageNumber));

		this.paginationComponent
			.getList()
			.pipe(untilDestroyed(this))
			.subscribe((transactions: T[]) => {
				if (!transactions.length) {
					return;
				}
				if (this.getTransactionDateFormat()) {
					this._map = Helper.getMap<T>(transactions, this.getTransactionDateFormat());
				} else {
					this._map = Helper.getMap<T>(transactions);
				}

				if (!equal(this._wallet.getId(), this.walletId)) {
					this._wallet = null;
				} // Setting this to null automatically re-sets the wallet in the getter using the correct walletId field
			});

		this.segmentService.page({
			// Hotifx for metals
			name: `${this.getCurrency().getSymbol(true).toUpperCase()} Wallet`,
			overridePath: `${this.trackingBasePath}/${this.getCurrency().getSymbol(true)}`
		});
		this.rudderstackService.page({
			// Hotifx for metals
			name: `${this.getCurrency().getSymbol(true).toUpperCase()} Wallet`,
			overridePath: `${this.trackingBasePath}/${this.getCurrency().getSymbol(true)}`
		});
	}

	public abstract openTransaction(params: Params): void;

	public initPagination(requestUrl: string, txType: typeof Transaction): void {
		this.paginationComponent.setUrl(requestUrl);
		this.paginationComponent.type = txType;
		this.paginationComponent.init();
	}

	public get map(): Map<string, T[]> {
		return this._map || new Map();
	}

	public isActive(t: T): boolean {
		return this.tid === t.getId();
	}

	public getCurrency(): C {
		if (!this._currency) {
			this.setCurrency();
		}
		return this._currency;
	}

	public setCurrency(): void {
		if (this.currencyService) {
			this._currency = this.currencyService.getItemById(
				this.walletService.getItemById(this.walletId).getCurrencyId()
			);
		}
	}

	public getWallet(): W {
		if (!this._wallet) {
			this._wallet = this.walletService?.getItemById(this.walletId);
		}
		return this._wallet;
	}

	public clearFilter(init = true): void {
		this.paginationComponent.clearFilter(init);
	}

	public filter(value: string, type = "type"): void {
		this._map = new Map();
		const filter = this.paginationComponent.getFilter();
		filter.delete("type");
		filter.delete("status");
		filter.delete("public_status");
		this.paginationComponent.setFilter(type, value);
	}

	public isType(filter: string, type: string = "type"): boolean {
		return this.paginationComponent.getFilter().get(type) === filter;
	}

	public noFilter(): boolean {
		return this.paginationComponent.getFilter().size === 0;
	}

	public getSymbolStatus(status: string): string {
		return "assets/images/svg/tx-status/icon-" + status + ".svg";
	}

	public getSymbolType(type: string): string {
		return "assets/images/svg/tx-type/icon-" + type + ".svg";
	}

	public tradingFirst(t: T): string {
		const trade = t.getTrade();
		if (trade.getType() === "buy") {
			return this.fiatSymbol(trade);
		}
		return this.coinSymbol(trade);
	}

	public tradingSecond(t: T): string {
		const trade = t.getTrade();
		if (trade.getType() === "sell") {
			return this.fiatSymbol(trade);
		}
		return this.coinSymbol(trade);
	}

	public getFilters(): string[] {
		return this.transactionFilters;
	}

	private coinSymbol(trade: Trade): string {
		return this.tradableListService.getItemById(trade.getCurrencyId()).getSymbol();
	}

	private fiatSymbol(trade: Trade): string {
		return this.fiatService.getItemById(trade.getFiatId()).getSymbol();
	}

	public hasProvider(t: T): boolean {
		return t.getTrade().hasProvider();
	}

	public getProvider(t: T): Provider {
		const trade = t.getTrade();
		if (trade.getType() === "buy") {
			return this.masterdataService.getPaymentOptionById(trade.getPaymentOptionId());
		}
		return null;
	}

	public hasFiatWallet(t: T): boolean {
		return t.getTrade().hasFiatWallet();
	}

	protected getTransactionDateFormat(): string {
		return this._transactionDateFormat;
	}

	protected setTransactionDateFormat(value: string): void {
		this._transactionDateFormat = value;
	}
}
